// RecapImagesButton.js — "Images for Instagram": the recap's slides onto the phone.
//
// On a phone the slides go to the native share sheet ("Save 10 images" to Photos, or straight to
// Instagram). A ZIP is useless there: iPhone parks it in Files, Android needs an app to open it.
// Where files cannot be shared (desktop), the button stays the ZIP download.
//
// ★ Two taps when the render is slow. share() is only allowed while the tap still counts as recent
// (a few seconds), and a cold recap takes up to ±10 s to draw. So the first tap fetches and tries;
// when the browser refuses (NotAllowedError) the images are kept and the button turns into
// "Tap to save the images", whose tap calls share() at once. Same trap as sharePhoto in utils.js.

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from '../../components/common/Icon';
import { apiRequest, canSharePhotos } from '../../utils';

const toFile = ({ name, data }) => {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type: 'image/jpeg' });
};

export default function RecapImagesButton({ teamId, className }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState('idle');   // idle | loading | ready
  const [shareable] = useState(canSharePhotos);
  const files = useRef(null);
  const lang = `?lang=${encodeURIComponent((i18n.language || '').slice(0, 2))}`;
  const zipUrl = `/api/team-recap/${teamId}/slides.zip${lang}`;
  const label = <><Icon name="download" /> {t('recapDownloadSlides')}</>;

  if (!shareable) return <a className={className} href={zipUrl}>{label}</a>;

  const share = async () => {
    try {
      await navigator.share({ files: files.current });
      setState('idle');
    } catch (err) {
      // Cancelled on the sheet, or the tap went stale while fetching: keep the images, next tap shares.
      setState(err && (err.name === 'AbortError' || err.name === 'NotAllowedError') ? 'ready' : 'idle');
    }
  };

  const onClick = async () => {
    if (state === 'loading') return;
    if (files.current) { share(); return; }
    setState('loading');
    try {
      const res = await apiRequest('GET', `/team-recap/${teamId}/slides.json${lang}`);
      const list = (res.slides || []).map(toFile);
      if (!list.length || !navigator.canShare({ files: list })) throw new Error('not shareable');
      files.current = list;
    } catch {
      setState('idle');
      window.location.href = zipUrl;   // the bytes still reach the person, as a ZIP
      return;
    }
    share();
  };

  return (
    <button type="button" className={className} onClick={onClick} disabled={state === 'loading'}>
      {state === 'loading' && t('recapImagesPreparing')}
      {state === 'ready' && <><Icon name="download" /> {t('recapImagesReady')}</>}
      {state === 'idle' && label}
    </button>
  );
}
